'use client'

import { useMemo } from 'react'

import { useTranslations } from 'next-intl'

import { FieldWrapper } from '@/components/form/field-wrapper'
import {
  type DateMatcher,
  DatePicker as DatePickerUI
} from '@/components/ui/date-picker'

import { formatDate, isFieldInvalid } from '@/utils'

import { useFieldContext } from './form'

export type DatePickerProps = {
  classNames?: {
    childrenWrapper?: string
    label?: string
    wrapper?: string
  }
  disabled?: boolean | DateMatcher | DateMatcher[]
  endIcon?: React.ReactNode
  endMonth?: Date
  format?: (date: Date) => string
  label?: string
  noChevron?: boolean
  placeholder?: string
  required?: boolean
  startIcon?: React.ReactNode
  startMonth?: Date
}

export const DatePicker: React.FC<DatePickerProps> = ({
  classNames,
  disabled,
  endIcon,
  endMonth,
  format,
  label,
  noChevron = false,
  placeholder,
  required,
  startIcon,
  startMonth
}) => {
  const t = useTranslations('components.form.datePicker')
  const resolvedPlaceholder = placeholder ?? t('selectDate')
  const field = useFieldContext<null | string>()

  // Convert ISO string to YYYY-MM-DD for DatePickerStandalone
  const displayValue = useMemo(() => {
    if (!field.state.value) return null
    const formatted = formatDate(field.state.value, {
      dateFormat: 'yyyy-MM-dd',
      disableRelativeFormatting: true,
      fallback: undefined
    })
    // Return null if date is invalid (formatted.date will be '-')
    return formatted.date === '-' ? null : formatted.date
  }, [field.state.value])

  const handleUpdate = (value: null | string) => {
    if (!value) {
      field.handleChange(null)
      return
    }
    // Convert YYYY-MM-DD to ISO string for form storage
    const date = new Date(value)
    if (!isNaN(date.getTime())) {
      field.handleChange(date.toISOString())
    } else {
      field.handleChange(null)
    }
  }

  return (
    <FieldWrapper
      classNames={classNames}
      field={field}
      label={label}
      required={required}
    >
      <DatePickerUI
        data-invalid={isFieldInvalid(field) || undefined}
        disabled={disabled}
        endIcon={endIcon}
        endMonth={endMonth}
        format={format}
        noChevron={noChevron}
        onBlur={field.handleBlur}
        onUpdate={handleUpdate}
        placeholder={resolvedPlaceholder}
        startIcon={startIcon}
        startMonth={startMonth}
        value={displayValue}
      />
    </FieldWrapper>
  )
}
